/**
 * Revenue Leakage Detector
 * Identifies where money is slipping through: waste, inefficiency, missed upsells.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Droplets, DollarSign, ChevronDown, AlertTriangle, TrendingDown, Utensils, Users, Clock, ShoppingCart, Zap, Target } from 'lucide-react';
import { getIndustryTerms } from '@/config/industryTerminology';

// Neutral, terminology-adapted template used for EVERY industry that doesn't
// have a bespoke dataset — so a real client never sees another sector's
// (e.g. QSR food-waste) leakage content. Categories are universally true for
// any business; the nouns adapt to the client's language.
const buildGenericLeakages = (t) => ([
  {
    id: 1, category: 'Missed Sales & Upsells', icon: ShoppingCart, totalLeak: 6200, severity: 'high',
    items: [
      { source: 'No upsell or cross-sell prompt at point of sale', amount: 3200, frequency: 'Every transaction', fix: `Introduce a simple add-on/upgrade prompt. Businesses that prompt consistently lift average ${t.revenue.toLowerCase()} per ${t.customerLower} by 8-12%.`, recoverable: 85 },
      { source: `${t.customers} not followed up after first purchase`, amount: 1900, frequency: 'Ongoing', fix: `Set a 24-48hr follow-up for new ${t.customersLower}. Repeat ${t.customersLower} cost 5-7x less than new acquisition.`, recoverable: 70 },
      { source: 'Discounting beyond policy', amount: 1100, frequency: 'Weekly', fix: 'Tighten discount approvals and track margin per sale — small unmanaged discounts compound quickly.', recoverable: 60 },
    ],
  },
  {
    id: 2, category: 'Wasted Spend', icon: TrendingDown, totalLeak: 3400, severity: 'medium',
    items: [
      { source: 'Unused or duplicate software subscriptions', amount: 1400, frequency: 'Monthly', fix: 'Audit recurring tool subscriptions; cancel unused seats and consolidate overlapping tools.', recoverable: 90 },
      { source: 'Supplier pricing not reviewed in 12+ months', amount: 1200, frequency: 'Ongoing', fix: 'Re-quote your top 3 suppliers annually. Even a 5% reduction on key inputs protects margin.', recoverable: 65 },
      { source: 'Stock / materials write-offs', amount: 800, frequency: 'Monthly', fix: 'Align ordering to demand and apply first-in-first-out to reduce obsolescence.', recoverable: 70 },
    ],
  },
  {
    id: 3, category: 'Labour Inefficiency', icon: Users, totalLeak: 2800, severity: 'medium',
    items: [
      { source: 'Overtime during quiet periods', amount: 1500, frequency: 'Weekly', fix: 'Align rosters to demand. Replace avoidable overtime with regular hours.', recoverable: 85 },
      { source: 'Manual admin that could be automated', amount: 1300, frequency: 'Daily', fix: 'Automate repetitive data entry and reporting to free up productive / billable hours.', recoverable: 75 },
    ],
  },
  {
    id: 4, category: 'Operational Gaps', icon: Clock, totalLeak: 2100, severity: 'low',
    items: [
      { source: 'Slow invoicing delaying cash', amount: 1200, frequency: 'Ongoing', fix: 'Invoice same-day on completion. Faster invoicing pulls cash forward and reduces debtor days.', recoverable: 80 },
      { source: 'Overdue accounts not chased', amount: 900, frequency: 'Monthly', fix: 'Automate reminders at 7/14/30 days overdue — consistent follow-up recovers most late payments.', recoverable: 85 },
    ],
  },
]);

const QSR_LEAKAGES = [
  {
    id: 1,
    category: 'Food Waste',
    icon: Utensils,
    totalLeak: 4200,
    severity: 'high',
    items: [
      { source: 'Over-prepped chicken (Footscray)', amount: 1400, frequency: '3-4x/week', fix: 'Switch to batch-cook model during slow periods. Reduce prep volume by 20% after 6pm.', recoverable: 85 },
      { source: 'Expired salad stock (Prospect)', amount: 980, frequency: '2x/week', fix: 'Reduce fresh produce order by 15%. Use FIFO labeling system. Promote salad combos on slow days.', recoverable: 70 },
      { source: 'Drive-through order remakes (network)', amount: 1820, frequency: 'Daily', fix: 'Install order confirmation screens at DT speaker. Current remake rate: 4.2%. Industry best: 1.8%.', recoverable: 60 },
    ],
  },
  {
    id: 2,
    category: 'Missed Upsells',
    icon: ShoppingCart,
    totalLeak: 8600,
    severity: 'high',
    items: [
      { source: 'No drink suggestion at register (Prospect, Footscray)', amount: 4200, frequency: 'Every transaction', fix: 'Implement suggestive sell script (already proven at Southport: +11% attachment). Projected gain: $4,200/mo.', recoverable: 90 },
      { source: 'No combo upgrade prompt on delivery orders', amount: 2800, frequency: '~180 orders/day', fix: 'Add "Make it a combo?" prompt to online ordering flow. A/B test showed 18% conversion on similar QSR brands.', recoverable: 75 },
      { source: 'Dessert attachment below benchmark', amount: 1600, frequency: 'Lunch & dinner', fix: 'Current dessert attachment: 8%. Benchmark: 14%. Counter display + verbal prompt at payment window.', recoverable: 65 },
    ],
  },
  {
    id: 3,
    category: 'Labour Inefficiency',
    icon: Users,
    totalLeak: 3800,
    severity: 'medium',
    items: [
      { source: 'Overtime penalty rates (Footscray weekends)', amount: 1900, frequency: 'Weekly', fix: 'Replace 2 overtime shifts with regular casuals. Saving: $420/weekend = $1,680/month.', recoverable: 88 },
      { source: 'Overlapping shifts during 2-4pm dead zone', amount: 1200, frequency: 'Daily (Penrith, Prospect)', fix: 'Reduce overlap by staggering start times. Redeploy saved hours to 11am-1pm peak.', recoverable: 80 },
      { source: 'Single-skill staff causing bottlenecks', amount: 700, frequency: 'Peak hours', fix: 'Cross-train 4 crew members on grill + register. Estimated training cost: $600 (one-time). Payback: 3 weeks.', recoverable: 70 },
    ],
  },
  {
    id: 4,
    category: 'Operational Gaps',
    icon: Clock,
    totalLeak: 2100,
    severity: 'low',
    items: [
      { source: 'Drive-through wait time > 3.5min (Footscray)', amount: 1200, frequency: 'Daily peak', fix: 'Every minute over target costs ~12 cars/hr in throughput. Pre-staging workflow would recover $1,200/mo.', recoverable: 75 },
      { source: 'Late opening causing lost breakfast traffic (Prospect)', amount: 900, frequency: '2x/week', fix: 'Prospect opens 10-15 min late ~2x/week. Automate alarm checklist for openers. Lost breakfast covers: ~22/month.', recoverable: 90 },
    ],
  },
];

const TECH_LEAKAGES = [
  {
    id: 1,
    category: 'Churn Revenue Loss',
    icon: TrendingDown,
    totalLeak: 12400,
    severity: 'high',
    items: [
      { source: 'Preventable churn — no CS outreach on inactive accounts', amount: 8400, frequency: 'Monthly', fix: 'Implement 14-day inactivity trigger for proactive CS calls. Historical save rate: 62%.', recoverable: 62 },
      { source: 'Failed payment recovery gap (3-day delay)', amount: 4000, frequency: 'Monthly', fix: 'Reduce dunning delay from 3 days to same-day retry. Add SMS notification for failed payments.', recoverable: 80 },
    ],
  },
  {
    id: 2,
    category: 'Missed Expansion Revenue',
    icon: ShoppingCart,
    totalLeak: 9200,
    severity: 'high',
    items: [
      { source: 'No upsell trigger when accounts hit usage limits', amount: 5800, frequency: 'Monthly', fix: 'Add in-app prompt when account reaches 80% of plan limits. Projected conversion: 22%.', recoverable: 75 },
      { source: 'Annual billing discount not promoted at renewal', amount: 3400, frequency: 'Quarterly', fix: 'Send "Save 20%" email 30 days before renewal. Industry conversion for annual switches: 35%.', recoverable: 70 },
    ],
  },
];

const severityColors = {
  high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700 border-red-200' },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
};

export const RevenueLeakageDetector = ({ industry = 'general' }) => {
  const [expandedId, setExpandedId] = useState(null);
  const t = getIndustryTerms(industry);
  // hospitality/qsr keep the rich food-service dataset; technology keeps its
  // SaaS dataset; EVERY other industry gets the neutral, terminology-adapted
  // template — never another sector's content.
  const leakages = t.key === 'hospitality' ? QSR_LEAKAGES
    : t.key === 'technology' ? TECH_LEAKAGES
    : buildGenericLeakages(t);
  const totalLeak = leakages.reduce((sum, l) => sum + l.totalLeak, 0);
  const totalRecoverable = leakages.reduce((sum, l) => sum + l.items.reduce((s, i) => s + (i.amount * i.recoverable / 100), 0), 0);

  return (
    <Card className="glass-card overflow-hidden" data-testid="revenue-leakage-detector">
      <div className="h-1 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Droplets className="w-5 h-5 text-rose-500" />
            Revenue Leakage
          </CardTitle>
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">
            ${totalLeak.toLocaleString()}/mo identified
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">Money slipping through operational gaps — with fix plans and recovery estimates</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary Bar */}
 <div className="grid grid-cols-1 gap-2">
          <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 text-center">
            <p className="text-[10px] uppercase text-rose-500 font-semibold">Total Leakage</p>
            <p className="text-xl font-bold text-rose-700">${totalLeak.toLocaleString()}<span className="text-xs font-normal">/mo</span></p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
            <p className="text-[10px] uppercase text-emerald-500 font-semibold">Recoverable</p>
            <p className="text-xl font-bold text-emerald-700">${Math.round(totalRecoverable).toLocaleString()}<span className="text-xs font-normal">/mo</span></p>
          </div>
        </div>

        {/* Leakage Categories */}
        {leakages.map(leak => {
          const cfg = severityColors[leak.severity];
          const Icon = leak.icon;
          const isExpanded = expandedId === leak.id;
          const catRecoverable = leak.items.reduce((s, i) => s + (i.amount * i.recoverable / 100), 0);

          return (
            <div
              key={leak.id}
              className={`border ${cfg.border} rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-sm`}
              onClick={() => setExpandedId(isExpanded ? null : leak.id)}
              data-testid={`leakage-${leak.id}`}
            >
              <div className={`flex items-center gap-3 p-3 ${cfg.bg}`}>
                <Icon className={`w-5 h-5 ${cfg.text} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{leak.category}</span>
                    <Badge className={`text-[10px] ${cfg.badge}`}>{leak.severity}</Badge>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">{leak.items.length} sources identified</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-rose-700">${leak.totalLeak.toLocaleString()}/mo</p>
                  <p className="text-[10px] text-emerald-600">${Math.round(catRecoverable).toLocaleString()} recoverable</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-2 border-t border-slate-100" data-testid={`leakage-detail-${leak.id}`}>
                  {leak.items.map((item, i) => (
                    <div key={i} className="bg-white/70 rounded-lg p-3 border border-slate-100 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-semibold text-slate-800">{item.source}</p>
                          <p className="text-[10px] text-slate-400">{item.frequency}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-rose-600">${item.amount.toLocaleString()}/mo</p>
                          <div className="flex items-center gap-1 justify-end">
                            <div className="w-10 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${item.recoverable}%` }} />
                            </div>
                            <span className="text-[10px] text-emerald-600">{item.recoverable}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                        <p className="text-[10px] uppercase text-slate-400 font-semibold mb-0.5">Fix Plan</p>
                        <p className="text-xs text-slate-700 leading-relaxed">{item.fix}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default RevenueLeakageDetector;
